import { NotificationService } from './../../../services/notification.service';
import { Component, OnInit, signal, effect, ChangeDetectorRef, computed } from '@angular/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { ConfirmationOverlayService } from '../../../services/confirmation-overlay.service';
import { TaxonomyService } from '../../../services/taxonomy.service';
import { TaxonomyApiService } from '../../../services/api-services/taxonomy-api.service';
import { Router } from '@angular/router';
import { UtcDateTimePipe } from '../../../pipes/utc-date-time.pipe';
import { DatePipe } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
  selector: 'app-taxonomy-version-actions',
  standalone: true,
  imports: [
    MatFormFieldModule,
    MatSelectModule,
    MatOptionModule,
    CommonModule,
    FormsModule,
    MatMenuModule,
    MatButtonModule,
    MatInputModule,
    UtcDateTimePipe,
    MatTooltipModule,
  ],
  providers: [DatePipe],
  templateUrl: './taxonomy-version-actions.component.html',
  styleUrl: './taxonomy-version-actions.component.scss',
})
export class TaxonomyVersionActionsComponent implements OnInit {
  searchText = '';
  selectedVersion = signal<any>(null);
  versionList = signal<any[]>([]);
  isReadyForPublish = signal<boolean>(false);
  isReadyForRelease = signal<boolean>(false);
  isCreateDraftVisible = computed(() => {
    const version = this.selectedVersion();
    return version?.status?.toLowerCase() !== 'draft' && version?.version !== null;
  });

  // Disable Create Draft button when selected version is a draft with DraftVisibleToAll !== true
  isCreateDraftDisabled = computed(() => {
    const version = this.selectedVersion();
    return version?.status?.toLowerCase() === 'draft' && version?.DraftVisibleToAll !== true;
  });

  private lastSelectedTaxonomy: string | null = null;

  constructor(
    public notificationService: NotificationService,
    private confirmationOverlayService: ConfirmationOverlayService,
    public taxonomyService: TaxonomyService,
    private taxonomyApiService: TaxonomyApiService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {
    // Initialize from sessionStorage
    this.initializeFromLocalStorage();
    // Set up storage event listener for cross-window changes
    window.addEventListener('storage', this.handleStorageChange.bind(this));
    // Set up interval to check for same-window changes
    setInterval(() => this.checkForLocalStorageChanges(), 1000);
  }

  private checkForLocalStorageChanges() {
    const currentTaxonomy = sessionStorage.getItem('selectedTaxonomy');
    if (currentTaxonomy !== this.lastSelectedTaxonomy) {
      this.lastSelectedTaxonomy = currentTaxonomy;
      if (currentTaxonomy) {
        this.updateUIFromTaxonomy(JSON.parse(currentTaxonomy));
        this.cdr.detectChanges();
      }
    }
  }

  private handleStorageChange(event: StorageEvent) {
    if (event.key === 'selectedTaxonomy' && event.newValue !== event.oldValue) {
      this.lastSelectedTaxonomy = event.newValue;
      if (event.newValue) {
        this.updateUIFromTaxonomy(JSON.parse(event.newValue));
        this.cdr.detectChanges();
      }
    }
  }

  private updateUIFromTaxonomy(taxonomy: any) {
    // Update version list
    if (taxonomy.version_history_details) {
      this.versionList.set(taxonomy.version_history_details);
    }

    // Update selected version - maintain selection based on record_id
    if (taxonomy.selected_taxonomy_version?.record_id) {
      const currentRecordId = taxonomy.selected_taxonomy_version.record_id;
      const matchingVersion = taxonomy.version_history_details?.find((v: any) => v.record_id === currentRecordId);

      if (matchingVersion) {
        // Keep the same record_id but update other properties
        this.selectedVersion.set({
          ...matchingVersion,
          record_id: currentRecordId,
        });
      }
    } else if (taxonomy.version_history_details?.length > 0) {
      // If no record_id match found, select first version
      this.selectedVersion.set(taxonomy.version_history_details[0]);
    }

    // Update button states
    this.updateButtonStates(taxonomy);

    // Update dropdown states
    this.updateDropdownStates(taxonomy);
  }

  private updateButtonStates(taxonomy: any) {
    const selectedVersion = taxonomy.selected_taxonomy_version;
    if (selectedVersion) {
      this.isReadyForPublish.set(selectedVersion.ReadyForPublish === true);
      this.isReadyForRelease.set(selectedVersion.ReadyForRelease === true);
    } else {
      this.isReadyForPublish.set(false);
      this.isReadyForRelease.set(false);
    }
  }

  private updateDropdownStates(taxonomy: any) {
    // Update version dropdown
    if (taxonomy.version_history_details?.length > 0) {
      this.versionList.set(taxonomy.version_history_details);
    } else {
      this.versionList.set([]);
      this.selectedVersion.set(null);
    }
  }

  ngOnInit() {
    // First, check if there's already a selected taxonomy and update UI immediately
    const currentSelectedTaxonomy = this.taxonomyService.getSelectedValue()();
    if (currentSelectedTaxonomy) {
      this.updateUIFromTaxonomy(currentSelectedTaxonomy);
      this.cdr.detectChanges();
    }

    // Subscribe to taxonomy selection changes using effect
    effect(() => {
      const selectedTaxonomy = this.taxonomyService.getSelectedValue()();
      if (selectedTaxonomy) {
        this.updateUIFromTaxonomy(selectedTaxonomy);
        this.cdr.detectChanges();
      }
    });
  }

  ngOnDestroy() {
    // Clean up the event listener when component is destroyed
    window.removeEventListener('storage', this.handleStorageChange.bind(this));
  }

  private reloadPage() {
    const currentUrl = this.router.url;
    this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this.router.navigate([currentUrl]);
    });
  }

  private initializeFromLocalStorage() {
    const selectedTaxonomy = sessionStorage.getItem('selectedTaxonomy');
    if (selectedTaxonomy) {
      const taxonomy = JSON.parse(selectedTaxonomy);
      this.updateUIFromTaxonomy(taxonomy);
    }
  }

  private handleTaxonomyChange(selectedTaxonomy: any) {
    if (
      selectedTaxonomy &&
      selectedTaxonomy.version_history_details &&
      selectedTaxonomy.version_history_details.length > 0
    ) {
      const updatedTaxonomy = {
        ...selectedTaxonomy,
        selected_taxonomy_version:
          selectedTaxonomy.selected_taxonomy_version ||
          (selectedTaxonomy.version_history_details[0].status !== 'draft'
            ? selectedTaxonomy.version_history_details[0]
            : null),
      };

      // Update sessionStorage and tracking variable
      const updatedTaxonomyStr = JSON.stringify(updatedTaxonomy);
      sessionStorage.setItem('selectedTaxonomy', updatedTaxonomyStr);
      this.lastSelectedTaxonomy = updatedTaxonomyStr;

      // Update UI
      this.updateUIFromTaxonomy(updatedTaxonomy);
      this.cdr.detectChanges();
    } else {
      this.versionList.set([]);
      this.selectedVersion.set(null);
      this.isReadyForPublish.set(false);
      this.isReadyForRelease.set(false);
      this.cdr.detectChanges();
    }
  }

  onSelectionChange(selected: any) {
    // Only update selected version when explicitly chosen from dropdown
    this.selectedVersion.set(selected);

    const currentValue = this.taxonomyService.getSelectedValue()();
    if (currentValue) {
      const updatedValue = {
        ...currentValue,
        selected_taxonomy_version: selected,
      };

      // Update the service and sessionStorage
      this.taxonomyService.setSelectedValue(updatedValue);
      const updatedTaxonomyStr = JSON.stringify(updatedValue);
      sessionStorage.setItem('selectedTaxonomy', updatedTaxonomyStr);
      this.lastSelectedTaxonomy = updatedTaxonomyStr;

      // Update UI
      this.updateUIFromTaxonomy(updatedValue);
      this.cdr.detectChanges();

      // Call get-all-entities API with the selected version's record_id
      if (selected?.record_id) {
        this.taxonomyApiService
          .getEntitiesV2ByTaxonomyId({
            taxonomy_id: currentValue.taxonomy_id,
            record_id: selected.record_id,
          })
          .subscribe({
            next: (response) => {
              // Handle the entities response
              this.cdr.detectChanges();

              // Trigger the updateTaxonomy$ observable to refresh the entity hub component
              this.taxonomyService.triggerTaxonomyUpdate();
            },
            error: (err) => {
              console.error('Failed to load entities:', err);
              this.notificationService.error('Load Entities', 'Failed to load entities for selected version');
            },
          });
      }
    }
  }

  get taxoSelectedParts() {
    const parts: { text: string; type: 'status' | 'version' | 'default' }[] = [];
    const currentVersion = this.selectedVersion();

    if (currentVersion?.status) {
      // Check if it's a draft with DraftVisibleToAll === true
      if (currentVersion.status.toLowerCase() === 'draft' && currentVersion.DraftVisibleToAll === true) {
        parts.push({ text: 'Draft in Review', type: 'status' });
      } else {
        parts.push({ text: currentVersion.status, type: 'status' });
      }
    }

    if (currentVersion?.version !== null && currentVersion?.version !== undefined) {
      parts.push({ text: `Version ${currentVersion.version}`, type: 'version' });
    }

    return parts.length ? parts : [{ text: 'Select Version', type: 'default' }];
  }

  filteredOptions() {
    const versions = this.versionList();
    if (!versions?.length) return [];

    return versions.filter(
      (v) =>
        !this.searchText ||
        (v.status && v.status.toLowerCase().includes(this.searchText.toLowerCase())) ||
        (v.version && String(v.version).includes(this.searchText))
    );
  }

  hasStatus(): boolean {
    const status = this.selectedVersion()?.status?.toLowerCase();
    return !!status && status !== '';
  }

  async clickedOnReleaseForReview() {
    await this.showConfirmationPopupForRelease();
  }

  async clickedOnPublish() {
    await this.showConfirmationPopupForPublish();
  }

  // Draft button
  async clickedOnCreateDraft() {
    try {
      await this.showConfirmationPopupForCreateDraft();
    } catch (error) {
      console.error('Error in clickedOnCreateDraft:', error);
    }
  }

  private async showConfirmationPopupForRelease() {
    const confirmed = await this.confirmationOverlayService.showConfirmation(
      'Release for Review Taxonomy',
      'Are you sure you want to release this taxonomy version? \n\n Note -  Others will be able to edit and publish it.'
    );

    if (confirmed) {
      this.releaseTaxonomy();
    }
  }

  private async showConfirmationPopupForPublish() {
    // Check if taxonomy has at least one queue attached
    const hasQueues = await this.checkTaxonomyHasQueues();

    if (!hasQueues) {
      // Show confirmation popup with queue validation message and only OK button
      const confirmed = await this.confirmationOverlayService.showConfirmation(
        'Publish Taxonomy',
        'There should be at least one queue name to publish it.',
        false, // Don't disable Yes button since we're not showing it
        true // Show only OK button
      );
      // Since we're showing only OK button, confirmed will always be false
      return;
    }

    // Original confirmation popup for valid taxonomy
    const confirmed = await this.confirmationOverlayService.showConfirmation(
      'Publish Taxonomy',
      'Are you sure you want to publish this taxonomy version? \n\n Note - This selected version of the taxonomy will be published to production and will impact live systems.'
    );

    if (confirmed) {
      this.publishTaxonomy();
    }
  }

  private async checkTaxonomyHasQueues(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      const localData = JSON.parse(sessionStorage.getItem('selectedTaxonomy') || '{}');
      const local_taxonomy_id = String(localData.taxonomy_id || '');
      const local_taxonomy_version = String(localData.selected_taxonomy_version?.record_id || '');

      if (!local_taxonomy_id || !local_taxonomy_version) {
        resolve(false);
        return;
      }

      const body = { taxonomy_id: local_taxonomy_id, record_id: local_taxonomy_version };

      this.taxonomyApiService.getSpecificTaxonomyV2ById(body).subscribe({
        next: (response) => {
          const taxonomyData = response.data;
          const linkedQueueGroups = Array.isArray(taxonomyData.LinkedQueueGroups) ? taxonomyData.LinkedQueueGroups : [];
          resolve(linkedQueueGroups.length > 0);
        },
        error: (err) => {
          console.error('Failed to fetch taxonomy data for queue validation:', err);
          resolve(false);
        },
      });
    });
  }

  // Show Draft Confirmation Popup
  private async showConfirmationPopupForCreateDraft() {
    try {
      const confirmed = await this.confirmationOverlayService.showConfirmation(
        'Create a draft of Taxonomy',
        'You are creating a new draft for editing. Are you sure? \n\n Note - A user can have only one active draft. Any changes in previous draft will be lost, unless published.'
      );
      if (confirmed) {
        this.createDraft();
      }
    } catch (error) {
      console.error('Error in showConfirmationPopupForCreateDraft:', error);
    }
  }

  private releaseTaxonomy() {
    const localData = JSON.parse(sessionStorage.getItem('selectedTaxonomy') || '{}');
    const local_taxonomy_version = String(localData.selected_taxonomy_version?.record_id || '');
    const local_taxonomy_id = String(localData.taxonomy_id || '');

    const body = { taxonomy_id: local_taxonomy_id, record_id: local_taxonomy_version };

    this.taxonomyApiService.releaseForReviewTaxonomy(body).subscribe({
      next: (res) => {
        this.taxonomyService.versionHistoryById();
        this.notificationService.success('Release', 'Taxonomy Released for Review!');
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Failed to fetch taxonomy', err);
        this.notificationService.error('release taxonomy', 'Taxonomy Failed to Released for Review');
      },
    });
  }

  private publishTaxonomy() {
    const localData = JSON.parse(sessionStorage.getItem('selectedTaxonomy') || '{}');
    const local_taxonomy_version = String(localData.selected_taxonomy_version?.record_id || '');
    const local_taxonomy_id = String(localData.taxonomy_id || '');

    const body = { taxonomy_id: local_taxonomy_id, record_id: local_taxonomy_version };

    this.taxonomyApiService.publishTaxonomyV2(body).subscribe({
      next: (res) => {
        this.taxonomyService.versionHistoryById();
        this.notificationService.success('publish taxonomy', 'Taxonomy published');
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Failed to fetch taxonomy', err);
        this.notificationService.error('publish taxonomy', 'Taxonomy failed to published');
      },
    });
  }

  private createDraft() {
    const localData = JSON.parse(sessionStorage.getItem('selectedTaxonomy') || '{}');
    const local_taxonomy_version = String(localData.selected_taxonomy_version?.record_id || '');
    const local_taxonomy_id = String(localData.taxonomy_id || '');

    const body = { taxonomy_id: local_taxonomy_id, record_id: local_taxonomy_version };

    this.taxonomyApiService.createDraft(body).subscribe({
      next: (res) => {
        const draft_record_id = res.data.record_id;

        // Step 2: Call get-version-history-api to get updated version history
        this.taxonomyApiService.getVersionHistoryById({ taxonomy_id: local_taxonomy_id }).subscribe({
          next: (versionHistoryRes) => {
            const responseData = versionHistoryRes.data;
            const updatedVersionHistory = responseData.version_history_details || [];

            // Step 3: Find the version with the new record_id from create-draft response
            const newDraftVersion = updatedVersionHistory.find((v: any) => v.record_id === draft_record_id);

            if (newDraftVersion) {
              // Update the taxonomy service with the new version history and selected version
              const updatedTaxonomy = {
                ...localData,
                version_history_details: updatedVersionHistory,
                selected_taxonomy_version: newDraftVersion,
              };

              // Update the service and sessionStorage
              this.taxonomyService.setSelectedValue(updatedTaxonomy);
              sessionStorage.setItem('selectedTaxonomy', JSON.stringify(updatedTaxonomy));
              this.lastSelectedTaxonomy = JSON.stringify(updatedTaxonomy);

              // Step 4: Call onSelectionChange with the found version object
              this.onSelectionChange(newDraftVersion);
            } else {
              console.error('New draft version not found in version history');
              this.notificationService.error('Draft Creation', 'Failed to find new draft version');
            }

            this.notificationService.success('draft', 'Draft created');
            this.cdr.detectChanges();
          },
          error: (versionHistoryErr) => {
            console.error('Failed to get version history after draft creation:', versionHistoryErr);
            this.notificationService.error('Draft Creation', 'Failed to get updated version history');
          },
        });
      },
      error: (err) => {
        console.error('Failed to create draft', err);
        this.notificationService.error('Draft Creation', 'Failed to create draft');
      },
    });
  }
}
